import { useState } from "react";
import { X, Trash2, UploadCloud, Loader2, Save } from "lucide-react";
import { api, formatError } from "@/lib/api";

const PhotoManager = ({ property, onSaved, onClose }) => {
  const [images, setImages] = useState(property.images || []);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    setErr("");
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const { data } = await api.post("/admin/upload", fd);
      setImages((v) => [...v, ...data.images]);
    } catch (error) {
      setErr(formatError(error.response?.data?.detail) || "No se pudieron subir las fotos.");
    }
    setUploading(false);
    e.target.value = "";
  };

  const removeImage = (i) => setImages((v) => v.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    setErr("");
    try {
      await api.put(`/properties/${property.id}`, { ...property, images });
      onSaved();
    } catch (error) {
      setErr(formatError(error.response?.data?.detail) || "No se pudo guardar.");
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center bg-ink/85 backdrop-blur-sm sm:items-center sm:p-6" data-testid="photo-manager-modal">
      <div className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-t-2xl border border-gold/25 bg-ink2 p-6 shadow-2xl sm:rounded-2xl md:p-8">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-serif text-2xl text-ivory">Fotos del inmueble</h3>
            <p className="mt-0.5 text-xs uppercase tracking-[0.2em] text-gold-subtle">
              Ref. {property.ref} · {property.title}
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid="photo-close-button"
            aria-label="Cerrar"
            className="rounded-full border border-gold/30 p-2 text-gold"
          >
            <X size={18} />
          </button>
        </div>

        <p className="mt-4 text-sm text-stone2">Añade fotos desde tu móvil u ordenador. La primera foto será la principal en el catálogo.</p>

        <label
          htmlFor="photo-manager-input"
          data-testid="photo-manager-dropzone"
          className="mt-5 flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-gold/35 bg-ink/60 px-6 py-10 text-center transition-colors hover:border-gold/70"
        >
          {uploading ? (
            <Loader2 size={30} className="animate-spin text-gold" />
          ) : (
            <UploadCloud size={30} className="text-gold" />
          )}
          <span className="text-sm font-medium text-ivory">
            {uploading ? "Subiendo fotos…" : "Toca para añadir fotos"}
          </span>
          <span className="text-xs text-stone2">Puedes seleccionar varias a la vez · JPG, PNG o WebP</span>
        </label>
        <input
          id="photo-manager-input"
          type="file"
          accept="image/*"
          multiple
          onChange={onFiles}
          data-testid="photo-manager-input"
          className="hidden"
        />

        {images.length > 0 && (
          <div className="mt-5 grid grid-cols-3 gap-3 sm:grid-cols-4">
            {images.map((im, i) => (
              <div key={i} className="group relative overflow-hidden rounded-lg border border-gold/25">
                <img src={im.startsWith("http") || im.startsWith("/api/") ? im : `/api/files/${im}`} alt="" className="aspect-square w-full object-cover" />
                {i === 0 && (
                  <span className="absolute left-1.5 top-1.5 rounded-full bg-gold px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-ink">
                    Principal
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => removeImage(i)}
                  data-testid={`photo-remove-${i}`}
                  title="Quitar foto"
                  className="absolute right-1.5 top-1.5 rounded-full bg-ink/90 p-1.5 text-red-400 transition-colors hover:bg-red-500/20"
                >
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>
        )}

        {err && (
          <p className="mt-4 text-sm text-red-400" data-testid="photo-manager-error">
            {err}
          </p>
        )}

        <div className="mt-7 flex gap-3">
          <button
            onClick={save}
            disabled={saving || uploading}
            data-testid="photo-save-button"
            className="btn-gold-metallic flex flex-1 items-center justify-center gap-2 rounded-full px-7 py-3 text-xs font-bold uppercase tracking-[0.18em] text-ink disabled:opacity-60"
          >
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Guardar fotos
          </button>
          <button
            onClick={onClose}
            data-testid="photo-cancel-button"
            className="rounded-full border border-gold/30 px-7 py-3 text-xs font-bold uppercase tracking-[0.18em] text-gold transition-colors hover:bg-gold/10"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default PhotoManager;
